// TabContent1.js
import { useTheme } from '@emotion/react';
import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { tokens } from '../../../../../theme';
import { showToastMessage } from '../../../../../utils/toasts';
import ProgressionTab from '../../../../theme/ProgressionBuilder/ProgressionTab';
import Text from '../../../../theme/text/Text';

import { InputDropdown } from "../../../../theme/dropdowns/InputDropdown";

import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import cx from 'classnames';
import { shipperTypeConfigurations } from '../../../../../data/shipperTypeConfigurations';
import { useGetCarriersSelectQuery } from '../../../../../newapi/carrier/carrierSlice';
import { useGetConfigInfoQuery } from '../../../../../newapi/shipper/shipperSlice';
import NumberInput from '../../../../theme/inputs/NumberInput';
import ShValidatedInput from '../../../../theme/inputs/ShValidatedInput';
import EMGeneralExplainerBox from "../../../ModalsPopover/explainers/EMGeneralExplainerBox";

/**
 * TabContent1 Component
 * 
 * Props:
 * - handleDataUpdate: Function, updates the data in the Redux state.
 * - handleNext: Function, navigates to the next tab.
 */
const Content = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const colors = tokens(theme.palette.mode);
    const data = { ...props.data };
    const dispatch = useDispatch();
    const [configShipperId, setConfigShipperId] = useState(null);
    const [transformedConfigInfo, setTransformedConfigInfo] = useState([]);
    const { data: configInfo } = useGetConfigInfoQuery({ id: configShipperId, returns: props.data?.allowReturn }, { skip: !configShipperId });
    const { data: carriers } = useGetCarriersSelectQuery();

    const shipperType = props.data.shipperType;
    const config = shipperTypeConfigurations[shipperType] || { fields: [], saveFormat: () => ({}) };
    const isPostNL = shipperType === 'postnl';

    useEffect(() => {
        if(props.data.shipperType === 'dhl' || props.data.shipperType === 'cirro' || props.data.shipperType === 'sendcloud' || props.data.shipperType === 'innosend' || props.data.shipperType === 'innosendv2' || props.data.shipperType === 'myparcelcom' || props.data.shipperType === 'zineps' || props.data.shipperType === 'vinologix'){
            setConfigShipperId(props.data.shipperId);
        }
    }, []);

    useEffect(() => {
        if(configInfo){
            if(props.data.shipperType === 'dhl'){
                const transformedConfigInfo = configInfo ? configInfo.configInfo.map(item => ({ key: item, value: item })) : [];
                setTransformedConfigInfo(transformedConfigInfo);
            } else if(props.data.shipperType === 'cirro'){
                const transformedConfigInfo = configInfo ? configInfo.configInfo.map(item => ({ key: item.EName, value: item.Code })) : [];
                setTransformedConfigInfo(transformedConfigInfo);
            } else if(props.data.shipperType === 'sendcloud'){
                setTransformedConfigInfo(configInfo?.configInfo);
            } else if(props.data.shipperType === 'innosend' || props.data.shipperType === 'innosendv2'){
                setTransformedConfigInfo(configInfo?.configInfo);
            } else if(props.data.shipperType === 'myparcelcom'){
                setTransformedConfigInfo(configInfo?.configInfo);
            } else if(props.data.shipperType === 'zineps'){
                setTransformedConfigInfo(configInfo?.configInfo);
            } else if(props.data.shipperType === 'vinologix'){
                setTransformedConfigInfo(configInfo?.configInfo);
            }
        }
    }, [configInfo]);
    

    const [formData, setFormData] = useState(() => {
        // Initialize with existing configuration data if available, otherwise use defaults
        const initialData = props.data.configuration && Object.keys(props.data.configuration).length > 0
            ? props.data.configuration
            : config.saveObject;
        return initialData || {};
    });

    // Ensure default values are set if formData fields are missing
    useEffect(() => {
        if (shipperType === 'hst' && !formData?.destination) {
            setFormData(prev => ({ ...prev, destination: 'netherlands' }));
        }
    }, [shipperType, formData?.destination]);

    // Validate exclusive field groups (e.g., Ampere services - only one can be active)
    useEffect(() => {
        if (!config?.exclusiveGroups || !formData) return;

        // Check each exclusive group
        Object.entries(config.exclusiveGroups).forEach(([groupName, groupConfig]) => {
            // Find all fields in this exclusive group
            const groupFields = config.fields.filter(field => field.exclusiveGroup === groupName);

            // Check which fields are active
            const activeFields = groupFields.filter(field => {
                const value = formData[field.name];

                // Determine if field is "active" based on its type
                if (field.type === 'boolean') {
                    return value === true;
                } else if (field.type === 'number') {
                    return value > 0;
                } else if (field.type === 'dropdown') {
                    return value && value !== '';
                } else if (field.type === 'text') {
                    return value && value.trim() !== '';
                }
                return false;
            });

            // Show warning if more fields are active than allowed
            if (activeFields.length > groupConfig.maxActive) {
                showToastMessage('warning', t(groupConfig.warningMessage), 7000);
            }
        });
    }, [formData, config, t]);

    const handleBoolChange = (name, value) => {
        if(value === "true"){
            setFormData(prevData => ({ ...prevData, [name]: true }));
        }else{
            setFormData(prevData => ({ ...prevData, [name]: false }));
        }
    };

    const handleInputChange = (name, value) => {
        setFormData(prevData => ({ ...prevData, [name]: value }));
    }
    
    const handleMultiDropdownChange = (baseList, name, values) => {
        setFormData(prevData => {
            const updatedArray = values.map(index => baseList[index].value);
            return { ...prevData, [name]: updatedArray };
        });
    };

    const handleDropdownChange = (baseList, name, value) => {
        const val = baseList[value]?.value;
        if(val) {
            setFormData(prevData => ({ ...prevData, [name]: val}));
        }

        // Clear pluscodes when destination changes for HST shipper
        if (shipperType === 'hst' && name === 'destination') {
            setFormData(prevData => ({ ...prevData, pluscodes: [] }));
        }

        // Clear carrier-bound selections when the carrier changes for Zineps
        if (shipperType === 'zineps' && name === 'carrierCode') {
            setFormData(prevData => ({ ...prevData, shippingMethod: "", returnShippingMethod: "", carrierContractId: "" }));
        }

        // Clear carrier-bound selections when the carrier changes for Vinologix.
        // A product or service from the other carrier is rejected with a 422.
        if (shipperType === 'vinologix' && name === 'carrier') {
            setFormData(prevData => ({
                ...prevData,
                carrierProduct: "",
                returnCarrierProduct: "",
                serviceReturnLabel: false,
                serviceAgeCheck: false,
                serviceSaturdayDelivery: false,
                serviceReturnNotHome: false,
                serviceOnlyRecipient: false,
                serviceSignature: false,
            }));
        }

        if (shipperType === 'innosend' || shipperType === 'innosendv2') {
            if (formData.shippingMethod && transformedConfigInfo?.shippingMethods) {
                const shippingMethod = transformedConfigInfo?.shippingMethods?.find(method => method.value === formData.shippingMethod);
                if (shippingMethod) {
                    const trykey = shippingMethod.key.split(' ')[0]?.toLowerCase();
                    setFormData(prevData => ({ ...prevData, type: trykey }));
                } else {
                    setFormData(prevData => ({ ...prevData, type: null }));
                }
            } else {
                setFormData(prevData => ({ ...prevData, type: null }));
            }
        }
    };

    const handleShipmentUnitCarrierChange = (unitCode, selectedCarrierIds) => {
        setFormData(prevData => ({
            ...prevData,
            shipmentUnits: {
                ...prevData.shipmentUnits,
                [unitCode]: selectedCarrierIds
            }
        }));
    };

    const selectedFieldIndex = (field) => {
        if(Array.isArray(formData[field.name])){
            const selectedIndexes = formData[field.name]
                .map(item => field.options.findIndex(option => option.value === item))
                .filter(index => index !== -1); // Filter out items not found in current options
            return selectedIndexes;
        } else {
            return [];
        }
    }

    const selectedCarrierIndices = (unitCode) => {
        if (formData.shipmentUnits && Array.isArray(formData.shipmentUnits[unitCode]) && carriers) {
            return formData.shipmentUnits[unitCode]
                .map(carrierId => carriers.findIndex(c => c.id === carrierId))
                .filter(index => index !== -1);
        }
        return [];
    };

    const filteredSizeAndWeightOptions = config.fields.filter(field => {
        if(isPostNL && field.name.includes("productCode_")){
            if (formData.parcelType === 'mailbox') {
                return field.name === 'productCode_MailboxParcel';
            } else {
                return field.name !== 'productCode_MailboxParcel';
            }
        }
        return true;
    });
    const isPostNLReturn = shipperType === 'postnl' && props.data?.allowReturn;
    const filteredFields = isPostNLReturn ? (config.returnFields || []) : config.fields.filter(field => {
        if (shipperType === 'postnl' && field.name.includes("productCode_")) {
            if (formData.parcelType === 'mailbox') {
                return field.name === 'productCode_MailboxParcel';
            } else {
                return field.name !== 'productCode_MailboxParcel';
            }
        }
    
        if (shipperType === 'dhl' ){
            if (field.name === 'sizeAndWeight') {
                return {
                    ...field,
                    options: field.options.filter(option => {
                        if(formData.shipmentType === 'business'){
                            return option.deliveryMethod === 'doordelivery' || option.deliveryMethod === 'both';
                        } else {
                            if(formData.deliveryMethod === 'mailbox'){
                                return (option.deliveryMethod === 'mailbox' || option.deliveryMethod === 'both') && option.value !== 'PALLET';
                            } else {
                                return option.deliveryMethod === 'doordelivery' && option.value !== 'PALLET';
                            }
                        }
                    })
                };
            } else if (field.name === 'deliveryMethod') {
                return {
                    ...field,
                    options: field.options.filter(option => formData.shipmentType !== 'business' || option.value !== 'mailbox')
                };
            }
        }

        if (shipperType === 'innosend' || shipperType === 'innosendv2') {
            if (formData.shippingMethod) {
                const shippingMethod = transformedConfigInfo.shippingMethods.find(method => method.value === formData.shippingMethod);
                if (shippingMethod) {
                    const trykey = shippingMethod.key.split(' ')[0]?.toLowerCase();
                    return field.name.startsWith(`${trykey}_`);
                } else {
                    return false;
                }
            } else {
                return false;
            }
        }

        if (shipperType === 'bol') {
            if (field.name === 'preferred_transporter' || field.name === 'preferred_label_type') {
                return formData.label_type === 'no' ? true : false;
            }
            if (field.name === 'only_sameday') {
                return formData.label_type === 'stockhub' || formData.label_type === 'no' ? true : false;
            }
        }
        return field;
    });

    // Zineps options come from configinfo; methods and contracts can be narrowed down by the chosen carrier
    const zinepsShippingMethods = transformedConfigInfo?.shippingMethods?.filter(method => !formData.carrierCode || method.carrierCode === formData.carrierCode) ?? [];
    const zinepsReturnShippingMethods = transformedConfigInfo?.returnShippingMethods?.filter(method => !formData.carrierCode || method.carrierCode === formData.carrierCode) ?? [];
    const zinepsContracts = transformedConfigInfo?.contracts?.filter(contract => !formData.carrierCode || contract.carrierCode === formData.carrierCode) ?? [];

    // Vinologix products come from configinfo and are narrowed down by the chosen carrier
    const vinologixProducts = transformedConfigInfo?.carrierProducts?.filter(product => !formData.carrier || product.carrier === formData.carrier) ?? [];

    // Sync formData to parent component
    useEffect(() => {
        props.handleDataUpdate({ configuration: formData });
    }, [formData]);

    const handleReturnOptionBoolChange = (name, value) => {
        const returnOptions = {
            ...(props.data.returnOptions || {}),
            [name]: value === "true"
        };
        props.handleDataUpdate({ returnOptions });
    };

    return (
        <>
            {/* Printless toggle - only for DHL return profiles */}
            {shipperType === 'dhl' && props.data.allowReturn && (
                <Box
                    display={"flex"}
                    flexDirection={"column"}
                    sx={{
                        gap:1,
                        maxWidth: 110,
                        "@media screen and (max-width:47.9375em)" : {
                            width:1,
                            flexDirection:"row",
                            alignItems:"center",
                            justifyContent:"space-between",
                        }
                    }}
                >
                    <Text>{t("printless")}</Text>
                    <ToggleButtonGroup
                        color={props.data.returnOptions?.printless ? "green" : "red"}
                        value={props.data.returnOptions?.printless ? "true" : "false"}
                        exclusive
                        onChange={(e) => handleReturnOptionBoolChange("printless", e.target.value)}
                        aria-label="Printless"
                        className={cx(`printless`, {
                            'active': props.data.returnOptions?.printless
                        })}
                        sx={{
                            background: colors.red[100],
                            padding:0.5,
                            borderRadius:5,
                            transition:"250ms",
                            "&.active" : {
                                background:colors.green[100],
                                transition:"250ms",
                            },
                        }}
                    >
                        <ToggleButton
                            sx={{
                                padding:0,
                                paddingX:2,
                                border:0,
                                lineHeight:"32px",
                                textTransform:"unset",
                                borderRadius:"32px !important",
                                color:colors.green[400],
                                transition:"250ms",
                                "&.Mui-selected,&.Mui-selected:hover" : {
                                    background:colors.red[400],
                                    color:`${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                }
                            }}
                            value="false">
                            {t('no')}
                        </ToggleButton>
                        <ToggleButton
                            sx={{
                                padding:0,
                                paddingX:2,
                                border:0,
                                lineHeight:"32px",
                                textTransform:"unset",
                                borderRadius:"32px !important",
                                color:colors.red[400],
                                transition:"250ms",
                                "&.Mui-selected,&.Mui-selected:hover" : {
                                    background:colors.green[400],
                                    color:`${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                }
                            }}
                            value="true"
                        >
                            {t('yes')}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            )}

            {transformedConfigInfo && shipperType === 'dhl' && (
                <Box key={"accountnumbers"} sx={{
                    display:"flex",
                    flexDirection:"column",
                    gap:1,
                    }}>
                        <Box
                            sx={{
                                display:'flex',
                            }}
                        >
                        <Text>{t("accountnumbers")}</Text>
                        <EMGeneralExplainerBox title={t("accountnumbers")} text={t("profile_accountnumbers_tooltip")}/>
                        </Box>
                        <InputDropdown
                            key={formData["accountnumbers"]}
                            name={"accountnumbers"}
                            multiple={true}
                            noSelection={t('choose_option')}
                            displayName="key"
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search")
                            }}
                            options={transformedConfigInfo ?? []}
                            selected={selectedFieldIndex({"name":"accountnumbers", "options":transformedConfigInfo})}
                            onChange={(selected) => handleMultiDropdownChange(transformedConfigInfo,"accountnumbers", selected)}
                            closeOnSelection={false}
                        />
                    </Box>
            )}
            {transformedConfigInfo && shipperType === 'cirro' && (
                <Box key={"accountnumbers"} sx={{
                    display:"flex",
                    flexDirection:"column",
                    gap:1,
                    }}>
                        <Text>{t("shipping_method")}<EMGeneralExplainerBox title={t("accountnumbers")} text={t("profile_accountnumbers_tooltip")}/></Text>
                        <InputDropdown
                            key={formData["shippingMethod"]}
                            name={"shippingMethod"}
                            noSelection={t('choose_option')}
                            displayName="key"
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search")
                            }}
                            options={transformedConfigInfo ?? []}
                            selected={transformedConfigInfo.findIndex(option => option.value === formData["shippingMethod"]) ?? -1}
                            onChange={(selected) => handleDropdownChange(transformedConfigInfo,"shippingMethod", selected)}
                        />
                    </Box>

            )}
            {transformedConfigInfo && shipperType === 'sendcloud' && (
                <>
                <Box key={"shippingMethod"} sx={{
                    display:"flex",
                    flexDirection:"column",
                    gap:1,
                    paddingTop:3,
                    }}>
                        <Text>{t("shipment_method")}</Text>
                        <InputDropdown
                            key={formData["shippingMethod"]}
                            name={"shippingMethod"}
                            noSelection={t('choose_option')}
                            displayName="key"
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search")
                            }}
                            options={transformedConfigInfo?.shippingOptions ?? []}
                            selected={transformedConfigInfo?.shippingOptions?.findIndex(option => option.value === formData["shippingMethod"]) ?? -1}
                            onChange={(selected) => handleDropdownChange(transformedConfigInfo?.shippingOptions,"shippingMethod", selected)}
                        />
                    </Box>
                    <Box key={"shippingMethodCustoms"} sx={{
                    display:"flex",
                    flexDirection:"column",
                    gap:1,
                    paddingTop:3,
                    }}>
                        <Text>{t("shipment_method_customs")}</Text>
                        <InputDropdown
                            key={formData["shippingMethodCustoms"]}
                            name={"shippingMethodCustoms"}
                            noSelection={t('choose_option')}
                            displayName="key"
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search")
                            }}
                            options={transformedConfigInfo?.shippingOptions ?? []}
                            selected={transformedConfigInfo?.shippingOptions?.findIndex(option => option.value === formData["shippingMethodCustoms"]) ?? -1}
                            onChange={(selected) => handleDropdownChange(transformedConfigInfo?.shippingOptions,"shippingMethodCustoms", selected)}
                        />
                    </Box>
                    <Box key={"senderAddresses"} sx={{
                    display:"flex",
                    flexDirection:"column",
                    gap:1,
                    paddingTop:3,
                    }}>
                        <Text>{t("senderAddresses")}</Text>
                        <InputDropdown
                            key={formData["senderAddress"]}
                            name={"senderAddress"}
                            noSelection={t('choose_option')}
                            displayName="key"
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search")
                            }}
                            options={transformedConfigInfo?.senderAddresses ?? []}
                            selected={transformedConfigInfo?.senderAddresses?.findIndex(option => option.value === formData["senderAddress"]) ?? -1}
                            onChange={(selected) => handleDropdownChange(transformedConfigInfo?.senderAddresses,"senderAddress", selected)}
                        />
                    </Box>
                    <Box key={"contracts"} sx={{
                    display:"flex",
                    flexDirection:"column",
                    gap:1,
                    paddingTop:3,
                    }}>
                        <Text>{t("contracts")}</Text>
                        <InputDropdown
                            key={formData["contract"]}
                            name={"contract"}
                            noSelection={t('choose_option')}
                            displayName="key"
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search")
                            }}
                            options={transformedConfigInfo?.contracts ?? []}
                            selected={transformedConfigInfo?.contracts?.findIndex(option => option.value === formData["contract"]) ?? -1}
                            onChange={(selected) => handleDropdownChange(transformedConfigInfo?.contracts,"contract", selected)}
                        />
                    </Box>
                </>
            )}
            {transformedConfigInfo && (shipperType === 'innosend' || shipperType === 'innosendv2') && (
                <Box key={"shippingMethod"} sx={{
                    display:"flex",
                    flexDirection:"column",
                    gap:1,
                    paddingTop:3,
                    }}>
                        <Text>{t("shipping_method")}</Text>
                        <InputDropdown
                            key={formData["shippingMethod"]}
                            name={"shippingMethod"}
                            noSelection={t('choose_option')}
                            displayName="key"
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search")
                            }}
                            options={transformedConfigInfo?.shippingMethods ?? []}
                            selected={transformedConfigInfo?.shippingMethods?.findIndex(option => option.value === formData["shippingMethod"]) ?? -1}
                            onChange={(selected) => handleDropdownChange(transformedConfigInfo?.shippingMethods,"shippingMethod", selected)}
                            closeOnSelection={true}
                        />
                    </Box>

            )}
            {transformedConfigInfo && shipperType === 'zineps' && (
                <>
                <Box key={"carrierCode"} sx={{
                    display:"flex",
                    flexDirection:"column",
                    gap:1,
                    paddingTop:3,
                    }}>
                        <Text>{t("zineps_carrier")}</Text>
                        <InputDropdown
                            key={formData["carrierCode"]}
                            name={"carrierCode"}
                            noSelection={t('choose_option')}
                            displayName="key"
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search")
                            }}
                            options={transformedConfigInfo?.carriers ?? []}
                            selected={transformedConfigInfo?.carriers?.findIndex(option => option.value === formData["carrierCode"]) ?? -1}
                            onChange={(selected) => handleDropdownChange(transformedConfigInfo?.carriers,"carrierCode", selected)}
                            closeOnSelection={true}
                        />
                    </Box>
                    <Box key={"shippingMethod"} sx={{
                    display:"flex",
                    flexDirection:"column",
                    gap:1,
                    paddingTop:3,
                    }}>
                        <Text>{t("shipping_method")}</Text>
                        <InputDropdown
                            key={formData["shippingMethod"]}
                            name={"shippingMethod"}
                            noSelection={t('choose_option')}
                            displayName="key"
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search")
                            }}
                            options={zinepsShippingMethods}
                            selected={zinepsShippingMethods.findIndex(option => option.value === formData["shippingMethod"]) ?? -1}
                            onChange={(selected) => handleDropdownChange(zinepsShippingMethods,"shippingMethod", selected)}
                            closeOnSelection={true}
                        />
                    </Box>
                    <Box key={"returnShippingMethod"} sx={{
                    display:"flex",
                    flexDirection:"column",
                    gap:1,
                    paddingTop:3,
                    }}>
                        <Text>{t("return_shipping_method")}</Text>
                        <InputDropdown
                            key={formData["returnShippingMethod"]}
                            name={"returnShippingMethod"}
                            noSelection={t('choose_option')}
                            displayName="key"
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search")
                            }}
                            options={zinepsReturnShippingMethods}
                            selected={zinepsReturnShippingMethods.findIndex(option => option.value === formData["returnShippingMethod"]) ?? -1}
                            onChange={(selected) => handleDropdownChange(zinepsReturnShippingMethods,"returnShippingMethod", selected)}
                            closeOnSelection={true}
                        />
                    </Box>
                    <Box key={"carrierContractId"} sx={{
                    display:"flex",
                    flexDirection:"column",
                    gap:1,
                    paddingTop:3,
                    }}>
                        <Text>{t("contract")}</Text>
                        <InputDropdown
                            key={formData["carrierContractId"]}
                            name={"carrierContractId"}
                            noSelection={t('choose_option')}
                            displayName="key"
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search")
                            }}
                            options={zinepsContracts}
                            selected={zinepsContracts.findIndex(option => option.value === formData["carrierContractId"]) ?? -1}
                            onChange={(selected) => handleDropdownChange(zinepsContracts,"carrierContractId", selected)}
                            closeOnSelection={true}
                        />
                    </Box>
                    <Box key={"senderId"} sx={{
                    display:"flex",
                    flexDirection:"column",
                    gap:1,
                    paddingTop:3,
                    }}>
                        <Text>{t("sender_address")}</Text>
                        <InputDropdown
                            key={formData["senderId"]}
                            name={"senderId"}
                            noSelection={t('choose_option')}
                            displayName="key"
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search")
                            }}
                            options={transformedConfigInfo?.senders ?? []}
                            selected={transformedConfigInfo?.senders?.findIndex(option => option.value === formData["senderId"]) ?? -1}
                            onChange={(selected) => handleDropdownChange(transformedConfigInfo?.senders,"senderId", selected)}
                            closeOnSelection={true}
                        />
                    </Box>
                </>
            )}
            {transformedConfigInfo && shipperType === 'vinologix' && (
                <>
                    <Box key={"senderAddress"} sx={{
                        display:"flex",
                        flexDirection:"column",
                        gap:1,
                        paddingTop:3,
                    }}>
                        <Text>{t("vinologix_sender_address")}</Text>
                        <InputDropdown
                            key={formData["senderAddress"]}
                            name={"senderAddress"}
                            noSelection={t('choose_option')}
                            displayName="key"
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search")
                            }}
                            options={transformedConfigInfo?.senderAddresses ?? []}
                            selected={transformedConfigInfo?.senderAddresses?.findIndex(option => option.value === formData["senderAddress"]) ?? -1}
                            onChange={(selected) => handleDropdownChange(transformedConfigInfo?.senderAddresses,"senderAddress", selected)}
                            closeOnSelection={true}
                        />
                        <Text light fontSize={12}>{t("vinologix_sender_address_help")}</Text>
                    </Box>
                    <Box key={"carrierProduct"} sx={{
                        display:"flex",
                        flexDirection:"column",
                        gap:1,
                        paddingTop:3,
                    }}>
                        <Text>{t("vinologix_carrier_product")}</Text>
                        <InputDropdown
                            key={formData["carrierProduct"]}
                            name={"carrierProduct"}
                            noSelection={t('choose_option')}
                            displayName="key"
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search")
                            }}
                            options={vinologixProducts}
                            selected={vinologixProducts.findIndex(option => option.value === formData["carrierProduct"]) ?? -1}
                            onChange={(selected) => handleDropdownChange(vinologixProducts,"carrierProduct", selected)}
                            closeOnSelection={true}
                        />
                    </Box>
                    <Box key={"returnCarrierProduct"} sx={{
                        display:"flex",
                        flexDirection:"column",
                        gap:1,
                        paddingTop:3,
                    }}>
                        <Text>{t("vinologix_return_carrier_product")}</Text>
                        <InputDropdown
                            key={formData["returnCarrierProduct"]}
                            name={"returnCarrierProduct"}
                            noSelection={t('choose_option')}
                            displayName="key"
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search")
                            }}
                            options={vinologixProducts}
                            selected={vinologixProducts.findIndex(option => option.value === formData["returnCarrierProduct"]) ?? -1}
                            onChange={(selected) => handleDropdownChange(vinologixProducts,"returnCarrierProduct", selected)}
                            closeOnSelection={true}
                        />
                    </Box>
                </>
            )}
            {transformedConfigInfo && shipperType === 'myparcelcom' && (
                <>
                <Box key={"serviceType"} sx={{
                    display:"flex",
                    flexDirection:"column",
                    gap:1,
                    paddingTop:3,
                    }}>
                        <Text>{t("serviceType")}</Text>
                        <InputDropdown
                            key={formData["serviceType"]}
                            name={"serviceType"}
                            noSelection={t('choose_option')}
                            displayName="key"
                            options={transformedConfigInfo?.configInfo?.serviceTypes ?? []}
                            selected={transformedConfigInfo?.configInfo?.serviceTypes?.findIndex(option => option.value === formData["serviceType"]) ?? -1}
                            onChange={(selected) => handleDropdownChange(transformedConfigInfo?.configInfo?.serviceTypes,"serviceType", selected)}
                            closeOnSelection={true}
                        />
                    </Box>
                    <Box key={"contract"} sx={{
                    display:"flex",
                    flexDirection:"column",
                    gap:1,
                    paddingTop:3,
                    }}>
                        <Text>{t("myparcel_contract")}</Text>
                        <InputDropdown
                            key={formData["contractId"]}
                            name={"contractId"}
                            noSelection={t('choose_option')}
                            displayName="key"
                            options={transformedConfigInfo?.configInfo?.contracts ?? []}
                            selected={transformedConfigInfo?.configInfo?.contracts?.findIndex(option => option.value === formData["contractId"]) ?? -1}
                            onChange={(selected) => handleDropdownChange(transformedConfigInfo?.configInfo?.contracts,"contractId", selected)}
                            closeOnSelection={true}
                        />
                    </Box>
                    <Box key={"shop"} sx={{
                    display:"flex",
                    flexDirection:"column",
                    gap:1,
                    paddingTop:3,
                    }}>
                        <Text>{t("myparcel_shop")}</Text>
                        <InputDropdown
                            key={formData["shopId"]}
                            name={"shopId"}
                            noSelection={t('choose_option')}
                            displayName="key"
                            options={transformedConfigInfo?.configInfo?.shops ?? []}
                            selected={transformedConfigInfo?.configInfo?.shops?.findIndex(option => option.value === formData["shopId"]) ?? -1}
                            onChange={(selected) => handleDropdownChange(transformedConfigInfo?.configInfo?.shops,"shopId", selected)}
                            closeOnSelection={true}
                        />
                    </Box>
                    </>

            )}

            {/* Preload field - NOT available for return profiles */}
            {!props.data?.allowReturn && (
                <Box
                    display={"flex"}
                    flexDirection={"row"}
                    sx={{
                        gap:1,
                        paddingTop:3,
                        justifyContent:"space-between",
                        alignItems:"center",
                        "@media screen and (max-width:47.9375em)" : {
                            width:1,
                            flexDirection:"row",
                            alignItems:"center",
                            justifyContent:"space-between",
                        }
                    }}
                >
                    <Text>
                        {t("preload")}
                        <EMGeneralExplainerBox title={t("preload")} text={t("preload_tooltip")}/>
                    </Text>

                    <ToggleButtonGroup
                        color={formData?.preload ? "green" : "red"}
                        value={formData?.preload ? "true" : "false"}
                        exclusive
                        onChange={(e) => handleBoolChange("preload", e.target.value)}
                        aria-label="Preload"

                        className={cx(`${formData?.preload}`, {
                            'active': formData?.preload
                        })}
                        sx={{
                            background: colors.red[100],
                            padding:0.5,
                            borderRadius:5,
                            transition:"250ms",
                            "&.active" : {
                                background:colors.green[100],
                                transition:"250ms",
                            },
                        }}
                    >
                        <ToggleButton
                            sx={{
                                padding:0,
                                paddingX:2,
                                border:0,
                                lineHeight:"32px",
                                textTransform:"unset",
                                borderRadius:"32px !important",
                                color:colors.green[400],
                                transition:"250ms",
                                "&.Mui-selected,&.Mui-selected:hover" : {
                                    background:colors.red[400],
                                    color:`${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                }
                            }}
                            value="false">
                            {t('no')}
                        </ToggleButton>
                        <ToggleButton
                            sx={{
                                padding:0,
                                paddingX:2,
                                border:0,
                                lineHeight:"32px",
                                textTransform:"unset",
                                borderRadius:"32px !important",
                                color:colors.red[400],
                                transition:"250ms",
                                "&.Mui-selected,&.Mui-selected:hover" : {
                                    background:colors.green[400],
                                    color:`${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                }
                            }}
                            value="true"
                        >
                            {t('yes')}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            )}

            {filteredFields.map((field, index) => {
                // Check if field is conditional and should be rendered
                if (field.conditional) {
                    const fieldValue = field.conditional.value;
                    let conditionMet = false;
                    
                    if (Array.isArray(fieldValue)) {
                        // For array conditions (e.g., serviceType: ['kleinpaket', 'auto'])
                        conditionMet = fieldValue.includes(formData[field.conditional.field]);
                    } else {
                        // For single value conditions
                        conditionMet = formData[field.conditional.field] === fieldValue;
                    }
                    
                    if (!conditionMet) return null;
                }

                switch (field.type) {
                    case 'text':
                        return (
                            <Box key={field.name} sx={{
                                display:"flex",
                                flexDirection:"column",
                                gap:1,
                                paddingTop:3,
                            }}>
                                <Text>{t(field.label)}{field.tooltip && <EMGeneralExplainerBox title={t(field.label)} text={t(field.tooltip)}/>}</Text>
                                <ShValidatedInput
                                    name={field.name}
                                    label={t(field.label)}
                                    value={formData[field.name] || ''}
                                    onChange={handleInputChange}
                                    //error={props.errors.name}
                                />
                                {/* <InputBase
                                    value={formData[field.name] || ''}
                                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                                /> */}
                            </Box>
                        );
                    case 'boolean':
                        return (
                            <Box 
                        display={"flex"} 
                        flexDirection={"row"}
                        sx={{
                            gap:1,
                            paddingTop:3,
                            //maxWidth: 110,
                            justifyContent:"space-between",
                            alignItems:"center",
                            "@media screen and (max-width:47.9375em)" : {
                                width:1,
                                flexDirection:"row",
                                alignItems:"center",
                                justifyContent:"space-between",
                            }
                        }}
                        >
                        <Text>
                        {t(field.label)}
                        {field.tooltip && <EMGeneralExplainerBox title={t(field.label)} text={t(field.tooltip)}/>}
                        </Text>
                        
                        <ToggleButtonGroup
                            color={formData[field.name] ? "green" : "red"}
                            value={formData[field.name] ? "true" : "false"}
                            exclusive
                            onChange={(e) => handleBoolChange(field.name, e.target.value)}
                            aria-label="Active"

                            className={cx(`${formData[field.name]}`, {
                                'active': formData[field.name]
                            })}
                            sx={{
                                background: colors.red[100],
                                padding:0.5,
                                borderRadius:5,
                                transition:"250ms",
                                "&.active" : {
                                    background:colors.green[100],
                                    transition:"250ms",
                                },
                            }}
                        >
                            <ToggleButton 
                                sx={{
                                    padding:0,
                                    paddingX:2,
                                    border:0,
                                    lineHeight:"32px",
                                    textTransform:"unset",
                                    borderRadius:"32px !important",
                                    color:colors.green[400],
                                    transition:"250ms",
                                    "&.Mui-selected,&.Mui-selected:hover" : {
                                        background:colors.red[400],
                                        color:`${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                    }
                                }}
                                value="false">
                                {t('no')}
                            </ToggleButton>
                            <ToggleButton 
                                sx={{
                                    padding:0,
                                    paddingX:2,
                                    border:0,
                                    lineHeight:"32px",
                                    textTransform:"unset",
                                    borderRadius:"32px !important",
                                    color:colors.red[400],
                                    transition:"250ms",
                                    "&.Mui-selected,&.Mui-selected:hover" : {
                                        background:colors.green[400],
                                        color:`${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                    }
                                }}
                                value="true"
                            >
                                {t('yes')}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                        );
                        case 'dropdown':
                            // Handle dynamic options from API
                            if(field.options === 'DYNAMIC' && field.dynamicKey){
                                const dynamicOptions = transformedConfigInfo?.[field.dynamicKey] || [];
                                return (
                                    <Box key={field.name} sx={{
                                        display:"flex",
                                        flexDirection:"column",
                                        gap:1,
                                        paddingTop:3,
                                    }}>
                                        <Text>{t(field.label)}{field.tooltip && <EMGeneralExplainerBox title={t(field.label)} text={t(field.tooltip)}/>}</Text>
                                        <InputDropdown
                                            key={formData[field.name]}
                                            name={field.name}
                                            noSelection={t('choose_option')}
                                            displayName="key"
                                            searchOptions={{
                                                enabled: true,
                                                placeholder: t("search")
                                            }}
                                            options={dynamicOptions}
                                            selected={dynamicOptions.findIndex(option => option.value === formData[field.name]) ?? -1}
                                            onChange={(selected) => handleDropdownChange(dynamicOptions,field.name, selected)}
                                            closeOnSelection
                                        />
                                    </Box>
                                );
                            }
                            if(field.translate){
                                const translatedOptions = field.options.map(option => ({
                                    ...option,
                                    label: t(option.label)
                                }));
                                field.options = translatedOptions;
                            }
                            return (
                                <Box key={field.name} sx={{
                                    display:"flex",
                                    flexDirection:"column",
                                    gap:1,
                                    paddingTop:3,
                                }}>
                                    <Text>{t(field.label)}{field.tooltip && <EMGeneralExplainerBox title={t(field.label)} text={t(field.tooltip)}/>}</Text>

                                    <InputDropdown
                                        key={formData[field.name]}
                                        name={field.name}
                                        useTranslation={field.translate}
                                        noSelection={t('choose_option')}
                                        displayName="label"
                                        searchOptions={field.options?.length > 20 ? {
                                            enabled: true,
                                            placeholder: t("search")
                                        } : undefined}
                                        options={field.options ?? ""}
                                        selected={field.options.findIndex(option => option.value === formData[field.name])}
                                        onChange={(selected) => handleDropdownChange(field.options,field.name, selected)}
                                        closeOnSelection
                                    />
                                </Box>
                            );
                        case 'multidropdown':
                            // Handle dynamic options based on destination (for HST pluscodes)
                            if (field.options === 'DYNAMIC_BY_DESTINATION' && shipperType === 'hst') {
                                const destination = formData.destination || 'netherlands';

                                // Don't render pluscodes dropdown for non-netherlands destinations
                                if(destination !== 'netherlands'){
                                    return null;
                                }

                                const pluscodeOptions = config.pluscodesByDestination?.[destination] || [];

                                return (
                                    <Box key={`${field.name}-${destination}`} sx={{
                                        display:"flex",
                                        flexDirection:"column",
                                        gap:1,
                                        paddingTop:3,
                                    }}>
                                        <Text>{t(field.label)}{field.helpText && <EMGeneralExplainerBox title={t(field.label)} text={t(field.helpText)}/>}</Text>
                                        <InputDropdown
                                            key={`${field.name}-dropdown-${destination}`}
                                            name={field.name}
                                            multiple={true}
                                            noSelection={t('choose_option')}
                                            displayName="label"
                                            searchOptions={{
                                                enabled: true,
                                                placeholder: t("search")
                                            }}
                                            options={pluscodeOptions}
                                            selected={selectedFieldIndex({...field, options: pluscodeOptions})}
                                            onChange={(selected) => handleMultiDropdownChange(pluscodeOptions, field.name, selected)}
                                            closeOnSelection={false}
                                        />
                                    </Box>
                                );
                            }

                            if(field.translate){
                                const translatedOptions = field.options.map(option => ({
                                    ...option,
                                    label: t(option.label)
                                }));
                                field.options = translatedOptions;
                            }
                            return (
                                <Box key={field.name} sx={{
                                    display:"flex",
                                    flexDirection:"column",
                                    gap:1,
                                    paddingTop:3,
                                }}>
                                    <Text>{t(field.label)}{field.tooltip && <EMGeneralExplainerBox title={t(field.label)} text={t(field.tooltip)}/>}</Text>
                                    <InputDropdown
                                        key={formData[field.name]}
                                        name={field.name}
                                        multiple={true}
                                        noSelection={t('choose_option')}
                                        displayName="label"
                                        options={field.options ?? []}
                                        selected={selectedFieldIndex(field)}
                                        onChange={(selected) => handleMultiDropdownChange(field.options,field.name, selected)}
                                        closeOnSelection={false}
                                    />
                                </Box>
                            );
                    case 'number':
                        return (
                            <Box key={field.name} sx={{
                                display:"flex",
                                flexDirection:"column",
                                gap:1,
                                paddingTop:3,
                            }}>
                                <Text>{t(field.label)}{field.helpText && <EMGeneralExplainerBox title={t(field.label)} text={t(field.helpText)}/>}</Text>
                                <NumberInput
                                    name={field.name}
                                    label={t(field.label)}
                                    value={formData[field.name] || field.default || 0}
                                    onChange={(value) => handleInputChange(field.name, value)}
                                    min={field.min}
                                    max={field.max}
                                    step={field.step}
                                    helperText={field.helpText ? t(field.helpText) : undefined}
                                />
                            </Box>
                        );
                    case 'info':
                        return (
                            <Box key={field.name} sx={{
                                display:"flex",
                                flexDirection:"column",
                                gap:1,
                                paddingTop:3,
                                p:2,
                                backgroundColor: colors.grey[100],
                                borderRadius:2,
                                border: `1px solid ${colors.grey[200]}`
                            }}>
                                <Text weight="bold">{t(field.label)}</Text>
                                {field.content && (
                                    <Box sx={{ display:"flex", flexDirection:"column", gap:0.5 }}>
                                        <Text size="small">Maximum Weight: {field.content.maxWeight}</Text>
                                        <Text size="small">Maximum Dimensions: {field.content.maxDimensions}</Text>
                                        <Text size="small">Minimum Dimensions: {field.content.minDimensions}</Text>
                                    </Box>
                                )}
                            </Box>
                        );
                    case 'shipmentunit_carriers':
                        if (!config.shipmentUnitSpecs || !carriers) return null;

                        return (
                            <Box key={field.name} sx={{
                                display:"flex",
                                flexDirection:"column",
                                gap:2,
                                paddingTop:3,
                            }}>
                                <Text>{t(field.label)}{field.helpText && <EMGeneralExplainerBox title={t(field.label)} text={t(field.helpText)}/>}</Text>

                                {Object.entries(config.shipmentUnitSpecs).map(([unitCode, unitSpec]) => (
                                    <Box key={unitCode} sx={{
                                        display:"flex",
                                        flexDirection:"column",
                                        gap:1,
                                        p:2,
                                        backgroundColor: colors.grey[50],
                                        borderRadius:1,
                                        border: `1px solid ${colors.grey[200]}`
                                    }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Text weight="bold">{unitSpec.name}</Text>
                                            <Box sx={{
                                                px: 1.5,
                                                py: 0.5,
                                                mx: 1,
                                                backgroundColor: colors.primary[100],
                                                borderRadius: 1,
                                                border: `1px solid ${colors.primary[200]}`
                                            }}>
                                                <Text size="small" color={colors.primary[700]}>
                                                    {unitSpec.displayInfo}
                                                </Text>
                                            </Box>
                                        </Box>
                                        <InputDropdown
                                            key={`${unitCode}-${formData.shipmentUnits?.[unitCode]?.length || 0}`}
                                            name={`shipmentUnit_${unitCode}`}
                                            multiple={true}
                                            noSelection={t('select_carriers')}
                                            displayName="name"
                                            searchOptions={{
                                                enabled: true,
                                                placeholder: t("search")
                                            }}
                                            options={carriers || []}
                                            selected={selectedCarrierIndices(unitCode)}
                                            onChange={(selected) => {
                                                const selectedIds = selected.map(index => carriers[index].id);
                                                handleShipmentUnitCarrierChange(unitCode, selectedIds);
                                            }}
                                            closeOnSelection={false}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        );
                    default:
                        return null;
                }
            })}
        </>
            
                
            
    );
};

const Configuration = (props) => {
    const { t } = useTranslation();

    // const schema = yup.object({
    //     street: yupRequired('street',t),
    //     house_nr: yupRequired('house_nr',t),
    //     city: yupRequired('city',t),
    //     postal: yupRequired('postal',t),
    // });

    return (
        <ProgressionTab 
        tabId="tabConfiguration" 
        subtitle={t('configuration')} 
        step={2}
        maxSteps={2}
        //schema={schema}
    >
        <Content />
    </ProgressionTab>
    )
}

export default Configuration;